import {
  Checkbox as HeadlessCheckbox,
  type CheckboxProps,
} from '@headlessui/react';
import clsx from 'clsx';
import Image from 'next/image';

type CheckboxImage = {
  src: string;
  alt: string;
};

const CheckboxGroup = ({
  className,
  children,
}: {
  className: string;
  children: React.ReactNode;
}) => {
  return (
    <div
      className={clsx(
        'cur-isolate cur-flex cur-flex-col cur-divide-y cur-divide-cool-gray cur-overflow-hidden',
        'cur-border cur-border-cool-gray cur-rounded-[20px]',
        className,
      )}
    >
      {children}
    </div>
  );
};

const Checkbox = ({
  children,
  className,
  image,
  ...props
}: CheckboxProps & {
  image?: CheckboxImage;
}) => {
  return (
    <HeadlessCheckbox
      {...props}
      className={clsx(
        'cur-flex cur-items-center cur-self-stretch cur-gap-[12px] cur-py-[12px] cur-pl-4 cur-pr-[12px] cur-bg-white',
        'cur-font-bold cur-text-[14px]',
        'cur-cursor-pointer',
        'hover:cur-bg-surface-primary focus:cur-bg-white',
        props.checked ? 'cur-text-vivid-purple' : 'cur-text-navy',
        className,
      )}
    >
      <>
        {children}
        {image ? (
          <div className="cur-relative cur-w-[120px] cur-h-[67.5px] cur-flex-shrink-0 cur-ml-auto">
            <Image
              src={image.src}
              alt={image.alt}
              width={120}
              height={67.5}
              className="cur-aspect-[120.00/67.50] cur-rounded-[12px] cur-object-cover cur-object-center"
            />
            {props.checked && (
              <div className="cur-absolute cur-w-full cur-h-full cur-flex cur-items-center cur-justify-center cur-inset-0 cur-rounded-[12px] cur-bg-[#F1EFFF]/60">
                <div className="cur-flex cur-w-[40px] cur-h-[40px] cur-rounded-full cur-bg-surface-brand cur-items-center cur-justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="25"
                    viewBox="0 0 24 25"
                    className="cur-text-white cur-fill-current"
                  >
                    <path
                      d="M17.9131 9.66309L11.1631 16.4131C10.797 16.7792 10.203 16.7792 9.83691 16.4131L6.08691 12.6631L7.41309 11.3369L10.5 14.4238L16.5869 8.33691L17.9131 9.66309Z"
                      fill="white"
                    />
                  </svg>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="cur-relative cur-ml-auto cur-shrink-0 cur-w-[24px] cur-h-[24px]">
            {props.checked && (
              <div className="cur-flex cur-w-[24px] cur-h-[24px] cur-rounded-full cur-bg-surface-brand cur-items-center cur-justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="25"
                  viewBox="0 0 24 25"
                  className="cur-text-white cur-fill-current"
                >
                  <path
                    d="M17.9131 9.66309L11.1631 16.4131C10.797 16.7792 10.203 16.7792 9.83691 16.4131L6.08691 12.6631L7.41309 11.3369L10.5 14.4238L16.5869 8.33691L17.9131 9.66309Z"
                    fill="white"
                  />
                </svg>
              </div>
            )}
          </div>
        )}
      </>
    </HeadlessCheckbox>
  );
};

export { CheckboxGroup, Checkbox };
